<template>
    <div class="alert-monitor-card relative cursor-pointer overflow-hidden" @click="showTaskDetail">
        <div class="amw-header">
            <span class="amw-icon">
                <bar-chart />
            </span>
            <span class="amw-title">
                <SkeletonLoader v-if="loading" style="width: 120px; height: 24px; border-radius: 0.5rem" />
                <template v-else>{{ data.title }}</template>
            </span>
            <div class="amw-menu" @click.stop="">
                <action-menu v-if="canBeEdited && hasAnyPermission('workorder.update')">
                    <dropdown-item v-if="isInProgress" @click.prevent="handleMoveToToDo">
                        {{ trans('dashboard.v2.alert_monitor.menu.move_to_to_do') }}
                    </dropdown-item>
                    <dropdown-item v-if="!isInProgress" @click.prevent="handleMoveToInProgress">
                        {{ trans('dashboard.v2.alert_monitor.menu.move_to_in_progress') }}
                    </dropdown-item>
                    <dropdown-item v-if="isToDo || isInProgress" @click.prevent="handleMoveToDone">
                        {{ trans('dashboard.v2.alert_monitor.menu.move_to_done') }}
                    </dropdown-item>
                    <template v-if="canBeDeleted && hasAnyPermission('workorder.delete')">
                        <dropdown-divider />
                        <dropdown-item @click.prevent="handleDelete">
                            <div class="d-flex align-items-center justify-content-between w-100">
                                {{ trans('general.action.delete') }}
                                <icon icon="cil-trash" />
                            </div>
                        </dropdown-item>
                    </template>
                </action-menu>
            </div>
        </div>
        <div class="amw-content">
            <div class="amw-main">
                <div class="amw-asset text-truncate">
                    <SkeletonLoader v-if="loading" style="width: 280px; height: 24px; border-radius: 0.5rem" />
                    <template v-else>{{ data.asset }}</template>
                </div>
                <div class="amw-location text-truncate">
                    <SkeletonLoader v-if="loading" style="width: 180px; height: 20px; border-radius: 0.5rem" />
                    <template v-else>{{ data.location }}</template>
                </div>
            </div>
            <div class="amw-divider" />
            <div class="amw-details">
                <div class="amw-row">
                    <span class="amw-label"> {{ trans('dashboard.v2.alert_monitor.inoperable_since') }}</span>
                    <span class="amw-value text-truncate">
                        <SkeletonLoader v-if="loading" style="width: 140px; height: 20px; border-radius: 0.5rem" />
                        <template v-else>{{ data.inoperableSince }}</template>
                    </span>
                </div>
                <div class="amw-row">
                    <span class="amw-label">
                        {{ data.assignee ? trans('dashboard.v2.alert_monitor.assigned_to') : trans('dashboard.v2.alert_monitor.reported_by') }}
                    </span>
                    <span class="amw-value text-truncate">
                        <SkeletonLoader v-if="loading" style="width: 100px; height: 20px; border-radius: 0.5rem" />
                        <template v-else>{{ data.assignee || data.reporter }}</template>
                    </span>
                </div>
                <div class="amw-row">
                    <span class="amw-label"> {{ trans('dashboard.v2.alert_monitor.ticket') }}</span>
                    <a class="amw-link text-truncate">
                        <SkeletonLoader v-if="loading" style="width: 120px; height: 20px; border-radius: 0.5rem" />
                        <template v-else>{{ data.ticket }}</template>
                    </a>
                </div>
            </div>
        </div>
        <div v-if="processing" class="absolute inset-0 flex items-center justify-center bg-black/10">
            <spinner />
        </div>
    </div>
</template>

<script setup>
import ActionMenu from '@/Components/ActionMenu.vue';
import DropdownItem from '@/Components/DropdownItem.vue';
import DropdownDivider from '@/Components/DropdownDivider.vue';
import Icon from '@/Components/Icon.vue';
import BarChart from '../../../../../icons/BarChart.vue';
import SkeletonLoader from '@/Components/SkeletonLoader.vue';
import Spinner from '@/Components/Spinner.vue';
import { computed } from 'vue';

const props = defineProps({
    loading: {
        type: Boolean,
        default: false,
    },
    data: {
        type: Object,
        required: true,
    },
    processing: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['move-to-progress', 'move-to-done', 'delete']);

const isToDo = computed(() => props.data.status === 'to_do');
const isInProgress = computed(() => props.data.status === 'in_progress');
const isDone = computed(() => props.data.status === 'done');

const canBeEdited = computed(() => !isDone.value && props.data.isEditable);
const canBeDeleted = computed(() => !isDone.value && props.data.isEditable);

const handleMoveToToDo = () => {
    emit('move-to-to-do', props.data);
};

const handleMoveToInProgress = () => {
    emit('move-to-progress', props.data);
};

const handleMoveToDone = () => {
    emit('move-to-done', props.data);
};

const handleDelete = () => {
    emit('delete', props.data);
};

const showTaskDetail = () => {
    emit('show-task-detail', props.data.ticketId);
};
</script>

<style scoped>
.alert-monitor-card {
    min-width: 330px;
    width: 330px;
    background: #eaf2ff;
    border-radius: 12px;
    padding-bottom: 0.75rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    color: var(--c-body-color);
    display: flex;
    flex-direction: column;
}

.amw-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0.75rem;
}

.amw-icon {
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.amw-icon svg rect:first-child {
    fill: var(--c-primary);
}

.amw-icon svg rect:not(:first-child) {
    fill: #fff;
}

.amw-title {
    font-size: 1rem;
    line-height: 1.125rem;
    font-weight: 500;
}

.amw-menu {
    margin-left: auto;
    display: flex;
    align-items: center;
}

.amw-menu svg {
    stroke: var(--c-disabled);
}

.amw-content {
    background: #fff;
    border-radius: 0.5rem;
    margin: 0 0.75rem;
    padding: 0;
}

.amw-main {
    padding: 0.75rem 0.75rem 0.375rem;
}

.amw-asset {
    font-size: 0.8125rem;
    line-height: 1.125rem;
    font-weight: 500;
}

.amw-location {
    font-size: 0.8125rem;
    line-height: 1.125rem;
    font-weight: 400;
}

.amw-divider {
    border-bottom: 1px solid var(--c-secondary);
    margin: 0;
}

.amw-details {
    padding: 0.375rem 0.75rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.125rem;
    font-weight: 500;
}

.amw-row {
    display: grid;
    grid-template-columns: 0.75fr 1fr;
    align-items: baseline;
    margin-bottom: 0.75rem;
}

.amw-row:last-child {
    margin-bottom: 0;
}

.amw-value {
    color: #8490a1;
}

.amw-link {
    color: #8490a1;
    text-decoration: underline;
    cursor: pointer;
}

::v-deep(.skeleton-loader__bone) {
    margin: 0 !important;
}
</style>
